import React, { useState, useCallback, useMemo, useRef } from 'react';
import './ProjectGroupSection.scss';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDraggableRows } from '../../../../shared/hooks/UseDraggableRows';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import SimpleBar from 'simplebar-react';
import { DataTable } from 'primereact/datatable';
import { ProjectGroup } from '../../../../shared/models/projects/ProjectGroup';
import { ProjectResponse } from '../../../../shared/models/projects/ProjectResponse';
import { makeSkeletonRows } from '../../../../shared/helpers/tableSkeleton';
import { getProjectsPage } from '../../../../shared/services/projects/ProjectService';
import { useProjectsListKpis } from '../../../../shared/api/hooks/project/stats/useProjectsListKpis';
import { ProjectListKpi } from '../../../../shared/api/services/project/stats/ProjectDailyStatsService';
import ProjectGroupHeader from '../project-group-header/ProjectGroupHeader';
import ModelTooltips from '../../../../shared/components/model-tooltips/ModelTooltips';
import NoResults from '../../../../shared/components/common/no-results/NoResults';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../../shared/utils/mobile';

const KPI_ID_CAP = 50;

/**
 * Factory to build DataTable column elements for a projects table section.
 * Receives items, kpis, and kpisLoading so each section can supply its own KPI data.
 */
export type ProjectSectionColumnBuilder = (
  items: ProjectResponse[],
  kpis: Record<number, ProjectListKpi>,
  kpisLoading: boolean
) => React.ReactElement[];

/** Mobilny odpowiednik `ProjectSectionColumnBuilder`: te same dane jako karty zamiast wierszy tabeli. */
export type ProjectSectionCardRenderer = (
  items: ProjectResponse[],
  kpis: Record<number, ProjectListKpi>,
  kpisLoading: boolean,
  showSkeleton: boolean
) => React.ReactNode;

export interface ProjectGroupSectionProps {
  group: ProjectGroup;
  buildColumns: ProjectSectionColumnBuilder;
  renderCards: ProjectSectionCardRenderer;
  kpiFrom: Date;
  kpiTo: Date;
  debouncedSearch: string;
  sortField: string;
  sortOrder: 'ASC' | 'DESC';
  onAssignSuccess: () => void;
  dragOverGroupId: number | null;
  draggedProjectId: number | null;
  onDragOver: (e: React.DragEvent, groupId: number) => void;
  onDrop: (e: React.DragEvent, groupId: number) => void;
  onDragLeave: (e: React.DragEvent) => void;
  /** Marks rows mid-mutation so they lock + show the centred spinner (see ProjectsPage). */
  rowClassName?: (rowData: ProjectResponse) => string;
}

const ProjectGroupSection: React.FC<ProjectGroupSectionProps> = ({
  group,
  buildColumns,
  renderCards,
  kpiFrom,
  kpiTo,
  debouncedSearch,
  sortField,
  sortOrder,
  onAssignSuccess,
  dragOverGroupId,
  draggedProjectId,
  onDragOver,
  onDrop,
  onDragLeave,
  rowClassName,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const bodyRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(true);
  // Lazy-mount the body on first expand and keep it mounted afterwards, so collapsed groups
  // that were never opened don't mount a DataTable, but re-expanding an opened group is instant
  // and the grid expand/collapse animation has content to animate.
  const [hasOpened, setHasOpened] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [first, setFirst] = useState(0);

  const isSearching = debouncedSearch.trim().length > 0;
  // While searching, force every group to fetch (with the search filter) — collapsed groups
  // normally don't fetch. Whether the body is SHOWN is decided further down, once the count is in.
  const shouldFetch = isSearching || !collapsed;

  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: [
      'projects-page-group',
      group.id,
      page,
      pageSize,
      debouncedSearch,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      getProjectsPage({
        groupId: group.id,
        page,
        pageSize,
        search: debouncedSearch || undefined,
        sort: sortField,
        order: sortOrder,
      }),
    enabled: shouldFetch,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const items: ProjectResponse[] = data?.items ?? [];
  const total: number = data?.total ?? 0;

  // A search that matches nothing in this group collapses it to its header instead of hiding the
  // section — the header is the drop target, and moving a project into a group you just searched
  // past is a normal thing to want. It stays clickable: expanding shows the "no results" body.
  const hasNoSearchMatches = isSearching && !isFetching && total === 0;
  const open = shouldFetch && (!hasNoSearchMatches || !collapsed);
  // Skeleton while a page/sort/first-load fetch is in flight — see ProjectsPage. Mutation refetches
  // (same key → isPlaceholderData false, data present) never skeleton.
  const showSkeleton = isFetching && (isPlaceholderData || !data);

  const pageProjectIds = useMemo(
    () => items.map((p) => p.id).filter((id): id is number => id != null).slice(0, KPI_ID_CAP),
    [items]
  );

  const { kpis, isLoading: kpisLoading } = useProjectsListKpis(pageProjectIds, kpiFrom, kpiTo);

  const handleToggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      if (prev) setHasOpened(true); // first expand → mount the body
      return !prev;
    });
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['projects-page-group'] });
    queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
    queryClient.invalidateQueries({ queryKey: ['project-groups'] });
    onAssignSuccess();
  }, [queryClient, onAssignSuccess]);

  const handlePageChange = (e: PaginatorPageChangeEvent) => {
    setPage(e.page);
    setFirst(e.first);
    setPageSize(e.rows);
  };

  const isDragOver = dragOverGroupId === group.id;

  // Searching with no matches → the shared "No results found" state; a genuinely empty
  // group keeps the compact text.
  const emptyTemplate = () =>
    isSearching ? (
      <NoResults />
    ) : (
      <NoResults icon="ico--folder" titleKey="emptyProjectGroup" descKey="" />
    );

  const builtColumns = useMemo(
    () => buildColumns(items, kpis, kpisLoading),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, kpis, kpisLoading, buildColumns]
  );

  // Make this section's rows draggable so a project can be dragged out to another group / ungrouped.
  useDraggableRows(bodyRef, open && !isMobile);

  return (
    <div
      className={`project-group-section${isDragOver ? ' project-group-section--drag-over' : ''}`}
      onDragOver={(e) => onDragOver(e, group.id)}
      onDrop={(e) => onDrop(e, group.id)}
      onDragLeave={onDragLeave}
    >
      <ProjectGroupHeader
        group={group}
        projectCount={isSearching ? total : group.projectCount}
        isCollapsed={!open}
        onToggleCollapse={handleToggleCollapse}
        onDeleteSuccess={handleDeleteSuccess}
      />
      <div
        className={`project-group-section__reveal${
          open ? ' project-group-section__reveal--expanded' : ''
        }`}
      >
        <div className="project-group-section__reveal-inner">
          {(hasOpened || open) && (
            <div className="project-group-section__body" ref={bodyRef}>
          {isMobile ? (
            !showSkeleton && items.length === 0 ? (
              emptyTemplate()
            ) : (
              renderCards(items, kpis, kpisLoading, showSkeleton)
            )
          ) : (
            <SimpleBar style={{ maxHeight: '100vh' }} autoHide={false}>
              <DataTable
                lazy
                scrollable
                value={showSkeleton ? makeSkeletonRows<ProjectResponse>(pageSize) : items}
                dataKey="id"
                tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
                tableClassName={`table${draggedProjectId != null ? ' table--draggable-rows' : ''}`}
                className="table-wrapper table-wrapper--accordion"
                emptyMessage={emptyTemplate}
                rowClassName={rowClassName}
              >
                {builtColumns}
              </DataTable>
            </SimpleBar>
          )}
          <Paginator
            className="pagination"
            first={first}
            rows={pageSize}
            totalRecords={total}
            rowsPerPageOptions={[10, 25, 50]}
            template={
              isMobile
                ? MOBILE_PAGINATOR_TEMPLATE
                : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
            }
            onPageChange={handlePageChange}
            pageLinkSize={isMobile ? 3 : 5}
          />
          {/* key tied to rendered rows — re-binds the icon tooltips after a skeleton/data swap (see
              ProjectsPage). */}
          <ModelTooltips key={pageProjectIds.join('-')} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectGroupSection;
